import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api'); // 

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});

// Intercepteur pour attacher automatiquement le JWT à chaque requête
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('tcvgd_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Intercepteur de réponse — si 401, déconnecter
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('tcvgd_token');
            localStorage.removeItem('tcvgd_user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me'),
    getAdmins: () => api.get('/auth/admins'),
    getParents: () => api.get('/auth/parents'),
    updateProfile: (data) => api.put('/auth/profile', data),
    updateAdmin: (id, data) => api.put(`/auth/admins/${id}`, data),
    updateAdminStatut: (id, statut) => api.put(`/auth/admins/${id}/statut`, { statut }),
    resetAdminPassword: (id) => api.post(`/auth/admins/${id}/reset-password`),
    forgotPassword: (data) => api.post('/auth/forgot-password', data),
};


// Cours / Groupes
export const coursesAPI = {
    getCalendarEvents: (params) => api.get('/courses/calendar', { params }),
    getGroupes: () => api.get('/courses/groupes'),
    getGroupeById: (id) => api.get(`/courses/groupes/${id}`),
    getSaisons: () => api.get('/courses/saisons'),
    getCourseTypes: () => api.get('/courses/cours-types'),
    getTerrains: () => api.get('/terrains'), // modifie pour pointer vers la nouvelle route
    moveEleve: (data) => api.put('/courses/groupes/move-eleve', data),
    saveGeneratedGroups: (data) => api.post('/courses/generate-groups', data),
    updateGroupe: (id, data) => api.put(`/courses/groupes/${id}`, data),
    deleteGroupe: (id) => api.delete(`/courses/groupes/${id}`),
    validateGroupes: (ids) => api.put('/courses/groupes/validate-batch', { groupe_ids: ids }),
    cancelCourse: (data) => api.post('/courses/groupes/cancel', data),
    getAnnulations: (params) => api.get('/courses/annulations', { params }),
    duplicateSaison: (id, data) => api.post(`/courses/saisons/${id}/duplicate`, data),
};

// Élèves / Inscriptions
export const studentsAPI = {
    getAll: (params) => api.get('/students', { params }),
    getMyStudents: () => api.get('/students/me'),
    getUnassigned: () => api.get('/students/unassigned'),
    getUnassignedStudents: () => api.get('/students/unassigned'), // Alias pour compatibilité
    create: (data) => api.post('/students', data),
    update: (id, data) => api.put(`/students/${id}`, data),
    delete: (id) => api.delete(`/students/${id}`),
    getGroupes: (id) => api.get(`/students/${id}/groupes`),
    getInscriptions: (id) => api.get(`/students/${id}/inscriptions`),
    createInscription: (id, data) => api.post(`/students/${id}/inscriptions`, data),
    updateInscriptionStatut: (inscriptionId, statut) => api.patch(`/students/${inscriptionId}/inscription/status`, { statut }),
    updateStatut: (id, statut) => api.put(`/students/${id}/statut`, { statut }),
    getAffinities: () => api.get('/students/affinites'),
    getActiveList: () => api.get('/students/active-list')
};

// Professeurs
export const professorsAPI = {
    getAll: () => api.get('/professors'),
    getById: (id) => api.get(`/professors/${id}`),
    getMe: () => api.get('/professors/me'),
    create: (data) => api.post('/professors', data),
    update: (id, data) => api.put(`/professors/${id}`, data),
    updateStatut: (id, statut) => api.put(`/professors/${id}/statut`, { statut }),
    getAllIndisponibilites: () => api.get('/professors/indisponibilites/all'),
    getMyIndisponibilites: () => api.get('/professors/me/indisponibilites'),
    signalerIndisponibilite: (data) => api.post('/professors/indisponibilites', data),
    traiterIndisponibilite: (id) => api.put(`/professors/indisponibilites/${id}/traiter`),
    updateCommentairePedagogique: (eleveId, commentaire_pedagogique) => api.put(`/professors/eleves/${eleveId}/commentaire`, { commentaire_pedagogique }),
};

// Terrains
export const terrainsAPI = {
    getAll: () => api.get('/terrains'),
    getById: (id) => api.get(`/terrains/${id}`),
    create: (data) => api.post('/terrains', data),
    update: (id, data) => api.put(`/terrains/${id}`, data),
    updateStatut: (id, statut) => api.put(`/terrains/${id}/statut`, { statut }),
    delete: (id) => api.delete(`/terrains/${id}`)
};

// Types de Cours
export const courseTypesAPI = {
    getAll: (saisonId) => api.get('/cours-types', { params: { saison_id: saisonId } }),
    getById: (id) => api.get(`/cours-types/${id}`),
    create: (data) => api.post('/cours-types', data),
    update: (id, data) => api.put(`/cours-types/${id}`, data),
    delete: (id) => api.delete(`/cours-types/${id}`)
};

// Niveaux
export const niveauxAPI = {
    proposer: (data) => api.post('/niveaux/proposer', data),
    getPending: () => api.get('/niveaux/en-attente'),
    getByEleve: (eleveId) => api.get(`/niveaux/eleve/${eleveId}`),
    repondre: (id, statut) => api.put(`/niveaux/${id}/repondre`, { statut })
};

// Présences (Appel)
export const presencesAPI = {
    getByGroupe: (groupeId, date) => api.get(`/presences/${groupeId}`, { params: { date } }),
    saveAppel: (groupeId, data) => api.post(`/presences/${groupeId}`, data),
    getByEleve: (eleveId) => api.get(`/presences/eleve/${eleveId}`),
    getHistorique: (groupeId, params) => api.get(`/presences/${groupeId}/historique`, { params }),
};

// Demandes de changement
export const changesAPI = {
    getAll: (statut) => api.get('/changes', { params: { statut } }),
    create: (data) => api.post('/changes', data),
    updateStatut: (id, statut) => api.patch(`/changes/${id}`, { statut }),
};

// Notifications
export const notificationsAPI = {
    getMine: () => api.get('/notifications'),
    markAsRead: (id) => api.put(`/notifications/${id}/read`),
    markAllRead: () => api.put('/notifications/mark-all-read'),
};

// Plages d'inscription
export const plagesAPI = {
    getAll: () => api.get('/plages'),
    create: (data) => api.post('/plages', data),
    update: (id, data) => api.put(`/plages/${id}`, data),
    delete: (id) => api.delete(`/plages/${id}`),
};

export default api;

